import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { todayLocalISO } from '../lib/dateUtils';
import api from '../lib/api';
import usePasteUpload from '../hooks/usePasteUpload';
import { turkishIncludes } from '../lib/turkishSearch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../components/ui/command';
import { Check } from 'lucide-react';
import VehicleDocuments from '../components/VehicleDocuments';
import { RentalFields } from '../components/vehicles/RentalFields';
import { FleetStatusCards } from '../components/vehicles/FleetStatusCards';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { 
  Plus,
  Car,
  Search,
  Loader2,
  Edit,
  Trash2,
  Building2,
  AlertTriangle,
  AlertCircle,
  Shield,
  Wrench,
  FileCheck,
  Calendar,
  Bell,
  FileText,
  Upload,
  Fuel,
  KeyRound,
  MapPin,
  BarChart3,
  X,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  Tag,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings2,
  Columns3,
  ImageIcon,
  SlidersHorizontal,
  UserPlus
} from 'lucide-react';
import { VehicleAssignDialog } from '../components/VehicleAssignDialog';
import { BulkVehicleAssignDialog } from '../components/BulkVehicleAssignDialog';
import { TransmissionBadge, FuelBadge } from '../components/VehicleBadges';

import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import { getBrandLogoComponent } from '../components/BrandLogos';

// Brand config: color classes
const BRAND_STYLES = {
  'mercedes': { color: 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' },
  'volkswagen': { color: 'bg-blue-600 text-white dark:bg-blue-500' },
  'vw': { color: 'bg-blue-600 text-white dark:bg-blue-500' },
  'ford': { color: 'bg-blue-700 text-white dark:bg-blue-400 dark:text-blue-950' },
  'renault': { color: 'bg-yellow-400 text-yellow-900 dark:bg-yellow-500 dark:text-yellow-950' },
  'peugeot': { color: 'bg-indigo-600 text-white dark:bg-indigo-400 dark:text-indigo-950' },
  'citroen': { color: 'bg-red-600 text-white dark:bg-red-400 dark:text-red-950' },
  'citroën': { color: 'bg-red-600 text-white dark:bg-red-400 dark:text-red-950' },
  'opel': { color: 'bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950' },
  'toyota': { color: 'bg-red-700 text-white dark:bg-red-500' },
  'bmw': { color: 'bg-sky-600 text-white dark:bg-sky-400 dark:text-sky-950' },
  'audi': { color: 'bg-zinc-700 text-white dark:bg-zinc-300 dark:text-zinc-900' },
  'fiat': { color: 'bg-rose-600 text-white dark:bg-rose-400 dark:text-rose-950' },
  'volvo': { color: 'bg-teal-700 text-white dark:bg-teal-400 dark:text-teal-950' },
  'iveco': { color: 'bg-cyan-700 text-white dark:bg-cyan-400 dark:text-cyan-950' },
  'dacia': { color: 'bg-emerald-600 text-white dark:bg-emerald-400 dark:text-emerald-950' },
  'hyundai': { color: 'bg-blue-500 text-white dark:bg-blue-400 dark:text-blue-950' },
  'nissan': { color: 'bg-orange-600 text-white dark:bg-orange-400 dark:text-orange-950' },
  'man': { color: 'bg-amber-700 text-white dark:bg-amber-500' },
  'scania': { color: 'bg-red-800 text-white dark:bg-red-500' },
  'daf': { color: 'bg-blue-800 text-white dark:bg-blue-400 dark:text-blue-950' },
  'renault': { color: 'bg-yellow-400 text-yellow-900 dark:bg-yellow-500 dark:text-yellow-950' },
};

const FIRM_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',
  'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700',
  'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
  'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
  'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-700',
  'bg-cyan-100 text-cyan-700 border-cyan-300 dark:bg-cyan-900/40 dark:text-cyan-300 dark:border-cyan-700',
  'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700',
  'bg-teal-100 text-teal-700 border-teal-300 dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-700',
  'bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700',
  'bg-pink-100 text-pink-700 border-pink-300 dark:bg-pink-900/40 dark:text-pink-300 dark:border-pink-700',
  'bg-lime-100 text-lime-700 border-lime-300 dark:bg-lime-900/40 dark:text-lime-300 dark:border-lime-700',
  'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300 dark:bg-fuchsia-900/40 dark:text-fuchsia-300 dark:border-fuchsia-700',
];

function getBrandStyle(brand) {
  if (!brand) return { color: 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200' };
  const key = brand.toLowerCase().trim();
  return BRAND_STYLES[key] || { color: 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200' };
}

function getFirmColor(firmName) {
  if (!firmName) return FIRM_COLORS[0];
  let hash = 0;
  for (let i = 0; i < firmName.length; i++) hash = firmName.charCodeAt(i) + ((hash << 5) - hash);
  return FIRM_COLORS[Math.abs(hash) % FIRM_COLORS.length];
}

export default function Vehicles() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getBrandIconUrl = (brand) => {
    if (!brand) return null;
    const key = brand.trim().toUpperCase();
    return brandIcons[key] ? `${API_URL}/api/brand-icons/${encodeURIComponent(key)}/image` : null;
  };
  
  // State
  const [searchParams, setSearchParams] = useSearchParams();
  // react-router's useSearchParams can be empty at first render for direct
  // URL navigations (e.g. /dashboard/vehicles?page=3). Reading from
  // window.location.search synchronously in the initializers avoids the race
  // where our URL-sync effect would strip the query before it's consumed.
  const initialParams = new URLSearchParams(window.location.search);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [firms, setFirms] = useState([]);
  const [summary, setSummary] = useState({ total: 0, with_warnings: 0, critical: 0 });
  // Filters — URL-backed so back-navigation preserves state
  const [selectedFirm, setSelectedFirm] = useState(() => initialParams.get('firm') || '');
  const [searchQuery, setSearchQuery] = useState(() => initialParams.get('q') || '');
  const [fuelFilter, setFuelFilter] = useState(() => initialParams.get('fuel') || '');
  const [brandFilter, setBrandFilter] = useState(() => initialParams.get('brand') || '');
  const [statusFilter, setStatusFilter] = useState(() => initialParams.get('status') || '');
  const [vergunningFilter, setVergunningFilter] = useState(() => initialParams.get('verg') || '');
  const [tireFilter, setTireFilter] = useState(() => initialParams.get('tire') || '');
  const [includeSold, setIncludeSold] = useState(() => initialParams.get('sold') === 'true');

  // Brand icons
  const [brandIcons, setBrandIcons] = useState({});
  const [brandIconsDialog, setBrandIconsDialog] = useState(false);
  const [brandIconUploadName, setBrandIconUploadName] = useState('');
  const [brandIconUploading, setBrandIconUploading] = useState(false);

  // Pagination — URL-backed
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(initialParams.get('page'), 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const s = parseInt(initialParams.get('size'), 10);
    return Number.isFinite(s) && s > 0 ? s : 10;
  });

  // Track the last visited vehicle so we can highlight its row and show a
  // contextual toast when the user returns via the Back button. Stored as
  // JSON `{id, label}` in sessionStorage (ephemeral).
  const readLastVisitedVeh = () => {
    try {
      const raw = sessionStorage.getItem('veh-last-visited');
      if (!raw) return null;
      if (raw.startsWith('{')) return JSON.parse(raw);
      return { id: raw, label: '' };
    } catch { return null; }
  };
  const [lastVisitedId, setLastVisitedId] = useState(() => readLastVisitedVeh()?.id || null);
  const vehToastFiredRef = useRef(false);

  const handleNavigateVehicle = (vehicle) => {
    // Accept either full vehicle object (preferred) or bare id for back-compat.
    const id = typeof vehicle === 'string' ? vehicle : vehicle?.id;
    if (!id) return;
    const label = typeof vehicle === 'object'
      ? (vehicle.license_plate || [vehicle.brand, vehicle.model].filter(Boolean).join(' ') || '')
      : '';
    try { sessionStorage.setItem('veh-last-visited', JSON.stringify({ id, label })); } catch {}
    setLastVisitedId(id);
    vehToastFiredRef.current = false;
    navigate(`/dashboard/vehicles/${id}`);
  };

  // Fire contextual toast once on return.
  useEffect(() => {
    if (vehToastFiredRef.current) return;
    const last = readLastVisitedVeh();
    if (last?.id && last?.label) {
      toast.info(`Son görüntülenen: ${last.label}`, {
        duration: 10000,
        action: {
          label: 'Tekrar Aç',
          onClick: () => navigate(`/dashboard/vehicles/${last.id}`),
        },
        cancel: {
          label: 'Kapat',
          onClick: () => {},
        },
        classNames: {
          cancelButton: '!bg-red-500 !text-white hover:!bg-red-600',
        },
      });
    }
    vehToastFiredRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filters/pagination → URL so back-navigation restores the list.
  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedFirm) next.set('firm', selectedFirm);
    if (searchQuery) next.set('q', searchQuery);
    if (fuelFilter) next.set('fuel', fuelFilter);
    if (brandFilter) next.set('brand', brandFilter);
    if (statusFilter) next.set('status', statusFilter);
    if (vergunningFilter) next.set('verg', vergunningFilter);
    if (tireFilter) next.set('tire', tireFilter);
    if (includeSold) next.set('sold', 'true');
    if (currentPage !== 1) next.set('page', String(currentPage));
    if (pageSize !== 10) next.set('size', String(pageSize));
    setSearchParams(next, { replace: true });
  }, [selectedFirm, searchQuery, fuelFilter, brandFilter, statusFilter, vergunningFilter, tireFilter, includeSold, currentPage, pageSize, setSearchParams]);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Dialog
  const [addDialog, setAddDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [documentsVehicle, setDocumentsVehicle] = useState(null);
  const [assignDialogVehicle, setAssignDialogVehicle] = useState(null);
  const [showChart, setShowChart] = useState(true);

  // Vergunning codes management
  const [vergCodes, setVergCodes] = useState([]);
  const [vergCodesDialog, setVergCodesDialog] = useState(false);
  const [newVergCode, setNewVergCode] = useState('');
  const [newVergFirm, setNewVergFirm] = useState('');
  const [newVergFile, setNewVergFile] = useState(null);
  const [vergSaving, setVergSaving] = useState(false);
  const [vergSearch, setVergSearch] = useState('');
  const [vergFirmFilter, setVergFirmFilter] = useState('');
  // Bulk vergunning assignment
  const [bulkVergDialog, setBulkVergDialog] = useState(false);
  const [bulkVergMode, setBulkVergMode] = useState('add'); // 'add' | 'remove'
  const [bulkVergSelected, setBulkVergSelected] = useState([]);
  const [bulkVergSaving, setBulkVergSaving] = useState(false);

  // Bulk driver assignment (Toplu Şoför Ata)
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);

  // Şub 2026 — Bulk vites güncelleme
  const [bulkTransDialog, setBulkTransDialog] = useState(false);
  const [bulkTransValue, setBulkTransValue] = useState('AUTOMATIC');
  const [bulkTransSaving, setBulkTransSaving] = useState(false);

  // Vergunning report (search/filter by code)
  const [vergReportDialog, setVergReportDialog] = useState(false);
  const [vergReportSummary, setVergReportSummary] = useState([]); // [{code, vehicle_count, document_count}]
  const [vergReportCode, setVergReportCode] = useState(''); // selected code
  const [vergReportData, setVergReportData] = useState(null); // {vehicles, total_vehicles, total_documents}
  const [vergReportLoading, setVergReportLoading] = useState(false);
  const [vergReportSearch, setVergReportSearch] = useState('');

  // Vergunning code: file preview + delete confirmation
  const [vergPreviewDoc, setVergPreviewDoc] = useState(null); // {id, file_name, file_type} or null
  const [vergDeleteConfirm, setVergDeleteConfirm] = useState(null); // {code, id, usage: {count, vehicles}} or null
  const [vergDeleting, setVergDeleting] = useState(false);

  // Column visibility (localStorage persisted)
  const COLUMN_STORAGE_KEY = 'vehicles_visible_columns';
  const ALL_COLUMNS = [
    { key: 'status', label: '', sortable: false, alwaysVisible: false },
    { key: 'plate', label: 'Plaka', sortable: true, sortField: 'license_plate', alwaysVisible: true },
    { key: 'firm', label: 'Firma', sortable: true, sortField: 'firm_name', adminOnly: true, alwaysVisible: false },
    { key: 'brandModel', label: 'Marka / Model', sortable: true, sortField: 'brand', alwaysVisible: false },
    { key: 'year', label: 'Yıl', sortable: true, sortField: 'year', alwaysVisible: false },
    { key: 'transmission', label: 'Vites', sortable: true, sortField: 'transmission', alwaysVisible: false },
    { key: 'fuel', label: 'Yakıt', sortable: true, sortField: 'fuel_type', alwaysVisible: false },
    { key: 'tire', label: 'Lastik', sortable: true, sortField: 'tire_type', alwaysVisible: false },
    { key: 'vergunning', label: 'Verg.', sortable: true, sortField: 'vergunning_number', alwaysVisible: false },
    { key: 'insurance', label: 'Sigorta', sortable: true, sortField: 'insurance_expiry_date', alwaysVisible: false },
    { key: 'inspection', label: 'Muayene', sortable: true, sortField: 'inspection_expiry_date', alwaysVisible: false },
    { key: 'maintenance', label: 'Bakım', sortable: true, sortField: 'next_maintenance_date', alwaysVisible: false },
  ];

  const getDefaultColumns = () => ALL_COLUMNS.map(c => c.key);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return getDefaultColumns();
  });

  const toggleColumn = (key) => {
    const col = ALL_COLUMNS.find(c => c.key === key);
    if (col?.alwaysVisible) return;
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const isColumnVisible = (key) => {
    const col = ALL_COLUMNS.find(c => c.key === key);
    if (col?.adminOnly && user?.role !== 'SUPER_ADMIN' && user?.role !== 'FULL_ADMIN') return false;
    return visibleColumns.includes(key);
  };

  // Sorting (localStorage persisted)
  const SORT_STORAGE_KEY = 'vehicles_sort';
  const [sortField, setSortField] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SORT_STORAGE_KEY))?.field || ''; } catch { return ''; }
  });
  const [sortDirection, setSortDirection] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SORT_STORAGE_KEY))?.dir || 'asc'; } catch { return 'asc'; }
  });

  const handleSort = (field) => {
    let nextField, nextDir;
    if (sortField === field) {
      if (sortDirection === 'asc') { nextField = field; nextDir = 'desc'; }
      else { nextField = ''; nextDir = 'asc'; }
    } else { nextField = field; nextDir = 'asc'; }
    setSortField(nextField);
    setSortDirection(nextDir);
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ field: nextField, dir: nextDir }));
  };
  
  // Form state
  const [formData, setFormData] = useState({
    firm_id: '',
    license_plate: '',
    brand: '',
    model: '',
    year: '',
    color: '',
    notes: '',
    // New fields
    chassis_number: '',
    load_capacity: '',
    fuel_type: '',
    permit_number: '',
    sticker: '',
    purchase_location: '',
    purchase_date: '',
    insurance_company: '',
    ownership_type: '',
    sale_date: '',
    rental_company: '', rental_start_date: '', rental_end_date: '', rental_monthly_fee: '', rental_daily_fee: '', replaces_vehicle_id: '',
    // Maintenance fields
    last_maintenance_date: '',
    next_maintenance_date: '',
    maintenance_km: '',
    next_maintenance_km: '',
    insurance_expiry_date: '',
    inspection_expiry_date: '',
    // Tire fields
    tire_type: '',
    tire_km: '',
    tire_date: '',
    // Şub 2026 — Vites tipi (AUTOMATIC | MANUAL)
    transmission: ''
  });

  // Fetch data
  useEffect(() => {
    fetchVehicles();
    fetchFirms();
    fetchBrandIcons();
    fetchVergCodes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFirm, includeSold]);

  // Scroll the previously visited vehicle row back into view + fade highlight
  // after 2.5s. Re-runs when vehicles (or pagination) changes.
  useEffect(() => {
    if (!lastVisitedId || vehicles.length === 0) return;
    const el = document.querySelector(`[data-row-id="${lastVisitedId}"]`);
    if (!el) return;
    const rafId = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => {
      setLastVisitedId(null);
      try { sessionStorage.removeItem('veh-last-visited'); } catch {}
    }, 2500);
    return () => { cancelAnimationFrame(rafId); clearTimeout(timer); };
  }, [vehicles, currentPage, lastVisitedId]);

  const fetchVergCodes = async () => {
    try {
      const res = await api.get('/vergunning-codes');
      if (res.data?.success) setVergCodes(res.data.data || []);
    } catch {}
  };

  const addVergCode = async () => {
    if (!newVergCode.trim() || !newVergFirm) return;
    setVergSaving(true);
    try {
      // Build the full code with the firm's prefix + integer
      const prefix = getFirmPrefix(newVergFirm);
      const fullCode = `${prefix}${newVergCode.trim()}`;
      const formData = new FormData();
      formData.append('firm_id', newVergFirm);
      formData.append('code', fullCode);
      if (newVergFile) formData.append('file', newVergFile);
      const res = await api.post('/vergunning-codes', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        setNewVergCode('');
        setNewVergFile(null);
        fetchVergCodes();
        toast.success(`Kod '${fullCode}' eklendi${newVergFile ? ' (+ belge)' : ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eklenemedi');
    } finally { setVergSaving(false); }
  };

  // Derive a short prefix per firm from the firm's name's first character.
  // Example: HYK → "H-", FAST-TRANS → "F-".
  const getFirmPrefix = (firmId) => {
    const firm = firms.find(f => f.id === firmId);
    if (!firm?.name) return '';
    const first = firm.name.trim()[0]?.toUpperCase() || '';
    return first ? `${first}-` : '';
  };

  const uploadFileToExistingCode = async (codeId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.put(`/vergunning-codes/${codeId}/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Belge yüklendi');
      fetchVergCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yüklenemedi');
    }
  };

  const deleteVergCode = async (codeId) => {
    // Check usage first. Show appropriate confirmation dialog before delete.
    try {
      const res = await api.get(`/vergunning-codes/${codeId}/usage`);
      const usage = res.data?.data || { count: 0, vehicles: [] };
      const code = vergCodes.find(c => c.id === codeId);
      setVergDeleteConfirm({ id: codeId, code: code?.code || '', usage, hasFile: !!code?.file_name });
    } catch {
      toast.error('Kullanım bilgisi alınamadı');
    }
  };

  const confirmDeleteVergCode = async () => {
    if (!vergDeleteConfirm) return;
    setVergDeleting(true);
    try {
      await api.delete(`/vergunning-codes/${vergDeleteConfirm.id}`);
      toast.success(`Kod '${vergDeleteConfirm.code}' silindi`);
      fetchVergCodes();
      fetchVehicles(); // refresh vehicle chips after removal
      setVergDeleteConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    } finally {
      setVergDeleting(false);
    }
  };

  const previewVergCodeFile = (code) => {
    if (!code.file_name) return;
    // Map to DocumentPreviewModal's expected shape. Our modal expects
    // `file_type` (e.g. "pdf" or "image/png"). We derive from content type.
    const ft = code.file_content_type || '';
    setVergPreviewDoc({
      id: code.id,
      file_name: code.file_name,
      file_type: ft,
    });
  };

  const updateVehicleVergCodes = async (vehicleId, codes, types) => {
    try {
      const payload = { codes };
      if (types) payload.types = types;
      await api.put(`/vehicles/${vehicleId}/vergunning-codes`, payload);
      setVehicles(prev => prev.map(v => v.id === vehicleId
        ? { ...v, vergunning_codes: codes, vergunning_types: types || v.vergunning_types || {} }
        : v));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const handleBulkVergSubmit = async () => {
    if (bulkVergSelected.length === 0 || selectedIds.size === 0) return;
    setBulkVergSaving(true);
    try {
      const res = await api.post('/vehicles/bulk-vergunning-codes', {
        vehicle_ids: Array.from(selectedIds),
        codes: bulkVergSelected,
        mode: bulkVergMode,
      });
      if (res.data?.success) {
        toast.success(res.data.message || 'Güncellendi');
        fetchVehicles();
        setBulkVergDialog(false);
        setBulkVergSelected([]);
        setSelectedIds(new Set());
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally { setBulkVergSaving(false); }
  };

  const openVergReport = async () => {
    setVergReportDialog(true);
    setVergReportCode('');
    setVergReportData(null);
    setVergReportSearch('');
    try {
      const res = await api.get('/vergunning-report');
      if (res.data?.success) setVergReportSummary(res.data.data?.by_code || []);
    } catch { toast.error('Rapor yüklenemedi'); }
  };

  const loadVergReportDetail = async (code) => {
    setVergReportCode(code);
    setVergReportLoading(true);
    setVergReportData(null);
    try {
      const res = await api.get(`/vergunning-report?code=${encodeURIComponent(code)}`);
      if (res.data?.success) setVergReportData(res.data.data);
    } catch { toast.error('Detay yüklenemedi'); }
    finally { setVergReportLoading(false); }
  };

  const exportVergReportCsv = () => {
    if (!vergReportData) return;
    const rows = [['Kod', 'Plaka', 'Marka', 'Model', 'Yıl', 'Firma', 'Belge Adı', 'Son Kullanma', 'Yüklenme']];
    vergReportData.vehicles.forEach(v => {
      if (v.documents.length === 0) {
        rows.push([vergReportData.code, v.license_plate || '', v.brand || '', v.model || '', v.year || '', v.firm_name || '', '(Belge yok)', '', '']);
      } else {
        v.documents.forEach(d => {
          rows.push([
            vergReportData.code, v.license_plate || '', v.brand || '', v.model || '', v.year || '',
            v.firm_name || '', d.file_name || '', d.expiry_date || '', (d.created_at || '').slice(0, 10),
          ]);
        });
      }
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vergunning-rapor-${vergReportData.code}-${todayLocalISO()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchBrandIcons = async () => {
    try {
      const response = await api.get('/brand-icons');
      if (response.data.success) {
        const map = {};
        (response.data.data || []).forEach(ic => { map[ic.brand] = ic; });
        setBrandIcons(map);
      }
    } catch {}
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFirm) params.append('firm_id', selectedFirm);
      if (includeSold) params.append('include_sold', 'true');
      
      const response = await api.get(`/vehicles?${params.toString()}`);
      if (response.data.success) {
        setVehicles(response.data.data || []);
        setSummary(response.data.summary || { total: 0, with_warnings: 0, critical: 0 });
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await api.get('/firms/');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching firms:', error);
    }
  };

  const openAddDialog = () => {
    setEditingVehicle(null);
    setFormData({
      firm_id: user?.role === 'FIRM_ADMIN' ? user?.firm_id : (selectedFirm || ''),
      license_plate: '',
      brand: '',
      model: '',
      year: '',
      color: '',
      notes: '',
      chassis_number: '',
      load_capacity: '',
      fuel_type: '',
      permit_number: '',
      sticker: '',
      purchase_location: '',
      purchase_date: '',
      insurance_company: '',
      ownership_type: '',
      sale_date: '',
      rental_company: '', rental_start_date: '', rental_end_date: '', rental_monthly_fee: '', rental_daily_fee: '', replaces_vehicle_id: '',
      last_maintenance_date: '',
      next_maintenance_date: '',
      maintenance_km: '',
      next_maintenance_km: '',
      insurance_expiry_date: '',
      inspection_expiry_date: '',
    // Tire fields
    tire_type: '',
    tire_km: '',
    tire_date: ''
    });
    setAddDialog(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      firm_id: vehicle.firm_id || '',
      license_plate: vehicle.license_plate || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      color: vehicle.color || '',
      notes: vehicle.notes || '',
      chassis_number: vehicle.chassis_number || '',
      load_capacity: vehicle.load_capacity || '',
      fuel_type: vehicle.fuel_type || '',
      permit_number: vehicle.permit_number || '',
      sticker: vehicle.sticker || '',
      purchase_location: vehicle.purchase_location || '',
      purchase_date: vehicle.purchase_date || '',
      insurance_company: vehicle.insurance_company || '',
      ownership_type: vehicle.ownership_type || '',
      sale_date: vehicle.sale_date || '',
      rental_company: vehicle.rental_company || '',
      rental_start_date: vehicle.rental_start_date || '',
      rental_end_date: vehicle.rental_end_date || '',
      rental_monthly_fee: vehicle.rental_monthly_fee ?? '',
      rental_daily_fee: vehicle.rental_daily_fee ?? '',
      replaces_vehicle_id: vehicle.replaces_vehicle_id || '',
      last_maintenance_date: vehicle.last_maintenance_date || '',
      next_maintenance_date: vehicle.next_maintenance_date || '',
      maintenance_km: vehicle.maintenance_km || '',
      next_maintenance_km: vehicle.next_maintenance_km || '',
      insurance_expiry_date: vehicle.insurance_expiry_date || '',
      inspection_expiry_date: vehicle.inspection_expiry_date || '',
      // Tire fields
      tire_type: vehicle.tire_type || '',
      tire_km: vehicle.tire_km || '',
      tire_date: vehicle.tire_date || '',
      transmission: vehicle.transmission || ''
    });
    setAddDialog(true);
  };

  const handleSave = async () => {
    if (!formData.license_plate) {
      toast.error(t('vehicles.plateRequired'));
      return;
    }
    if (!formData.firm_id && user?.role !== 'FIRM_ADMIN') {
      toast.error(t('vehicles.firmRequired'));
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        maintenance_km: formData.maintenance_km ? parseInt(formData.maintenance_km) : null,
        next_maintenance_km: formData.next_maintenance_km ? parseInt(formData.next_maintenance_km) : null,
        // Clean empty strings to null
        chassis_number: formData.chassis_number || null,
        load_capacity: formData.load_capacity || null,
        fuel_type: formData.fuel_type || null,
        permit_number: formData.permit_number || null,
        sticker: formData.sticker || null,
        purchase_location: formData.purchase_location || null,
        purchase_date: formData.purchase_date || null,
        insurance_company: formData.insurance_company || null,
        ownership_type: formData.ownership_type || null,
        sale_date: formData.sale_date || null,
        ...(formData.ownership_type === 'Kiralık' ? {
          rental_company: formData.rental_company || null,
          rental_start_date: formData.rental_start_date || null,
          rental_end_date: formData.rental_end_date || null,
          rental_monthly_fee: formData.rental_monthly_fee === '' || formData.rental_monthly_fee == null ? null : parseFloat(formData.rental_monthly_fee),
          rental_daily_fee: formData.rental_daily_fee === '' || formData.rental_daily_fee == null ? null : parseFloat(formData.rental_daily_fee),
          replaces_vehicle_id: formData.replaces_vehicle_id || null,
        } : {
          rental_company: null, rental_start_date: null, rental_end_date: null,
          rental_monthly_fee: null, rental_daily_fee: null, replaces_vehicle_id: null,
        }),
        // Tire fields
        tire_type: formData.tire_type && formData.tire_type !== 'NONE' ? formData.tire_type : null,
        tire_km: formData.tire_km ? parseInt(formData.tire_km) : null,
        tire_date: formData.tire_date || null,
      };

      if (editingVehicle) {
        await api.put(`/vehicles/${editingVehicle.id}`, payload);
        toast.success(t('vehicles.vehicleUpdated'));
      } else {
        await api.post('/vehicles', payload);
        toast.success(t('vehicles.vehicleAdded'));
      }

      setAddDialog(false);
      fetchVehicles();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(' · ') : (detail || t('vehicles.errorOccurred')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}`);
      toast.success(t('vehicles.vehicleDeleted'));
      setDeleteConfirm(null);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('vehicles.deleteError'));
    }
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter(v => {
    // Search filter
    if (searchQuery) {
      const matchesSearch = (
        turkishIncludes(v.license_plate || '', searchQuery) ||
        turkishIncludes(v.brand || '', searchQuery) ||
        turkishIncludes(v.model || '', searchQuery) ||
        turkishIncludes(v.color || '', searchQuery) ||
        turkishIncludes(v.chassis_number || '', searchQuery) ||
        turkishIncludes(v.fuel_type || '', searchQuery) ||
        turkishIncludes(v.ownership_type || '', searchQuery) ||
        turkishIncludes(v.firm_name || '', searchQuery)
      );
      if (!matchesSearch) return false;
    }
    // Fuel type filter
    if (fuelFilter && v.fuel_type !== fuelFilter) return false;
    // Brand filter (from chart click)
    if (brandFilter && v.brand !== brandFilter) return false;
    // Status filter
    if (statusFilter === 'critical' && !v.has_critical) return false;
    if (statusFilter === 'warning' && !v.has_warnings) return false;
    if (statusFilter === 'ok' && (v.has_warnings || v.has_critical)) return false;
    // Vergunning filter
    const hasVerg = (v.vergunning_in_vehicle !== null && v.vergunning_in_vehicle !== undefined) || v.vergunning_number;
    if (vergunningFilter === 'yes' && !hasVerg) return false;
    if (vergunningFilter === 'no' && hasVerg) return false;
    // Tire filter
    if (tireFilter && tireFilter !== 'all') {
      if (tireFilter === 'has' && !v.tire_type) return false;
      if (tireFilter === 'none' && v.tire_type) return false;
      if (tireFilter !== 'has' && tireFilter !== 'none' && v.tire_type !== tireFilter) return false;
    }
    return true;
  });

  // Pagination
  const sortedVehicles = [...filteredVehicles].sort((a, b) => {
    if (!sortField) return 0;
    let aVal = a[sortField];
    let bVal = b[sortField];
    // Handle nulls
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    // Date fields
    if (sortField.includes('date')) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    // Numeric fields
    else if (sortField === 'year' || sortField === 'tire_km' || sortField === 'doc_count' || sortField === 'vergunning_number') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }
    // String fields
    else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const totalPages = Math.ceil(sortedVehicles.length / pageSize);
  const paginatedVehicles = sortedVehicles.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  
  // Reset page when filters change. Compare against previous values so the
  // URL-initialized currentPage (e.g. ?page=3) isn't clobbered back to 1 by
  // React 18 strict-mode's double-mount or by any other re-run where the
  // filter values haven't actually changed.
  const prevFiltersRef = useRef({
    searchQuery, fuelFilter, brandFilter, statusFilter,
    vergunningFilter, tireFilter, selectedFirm,
  });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const changed =
      prev.searchQuery !== searchQuery ||
      prev.fuelFilter !== fuelFilter ||
      prev.brandFilter !== brandFilter ||
      prev.statusFilter !== statusFilter ||
      prev.vergunningFilter !== vergunningFilter ||
      prev.tireFilter !== tireFilter ||
      prev.selectedFirm !== selectedFirm;
    prevFiltersRef.current = {
      searchQuery, fuelFilter, brandFilter, statusFilter,
      vergunningFilter, tireFilter, selectedFirm,
    };
    if (changed) setCurrentPage(1);
  }, [searchQuery, fuelFilter, brandFilter, statusFilter, vergunningFilter, tireFilter, selectedFirm]);

  // Safety clamp: after vehicles/filters load, if the URL-restored currentPage
  // is beyond the available pages (e.g. filter narrowed the result set since
  // the user last visited), snap to the last valid page. Without this a user
  // returning to ?page=3 when only 1 page exists saw an empty table.
  useEffect(() => {
    if (loading) return;
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [loading, totalPages, currentPage]);
  
  // Reset selection when data changes
  useEffect(() => { setSelectedIds(new Set()); }, [vehicles, searchQuery, fuelFilter, statusFilter]);

  // Bulk selection helpers
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedVehicles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedVehicles.map(v => v.id)));
    }
  };

  const handleBrandIconUpload = async (file, brand) => {
    if (!file || !brand) return;
    setBrandIconUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('brand', brand.trim().toUpperCase());
      await api.post('/brand-icons', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${brand.toUpperCase()} ikonu yüklendi`);
      fetchBrandIcons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yükleme hatası');
    } finally { setBrandIconUploading(false); }
  };

  const handleBrandIconDelete = async (brand) => {
    try {
      await api.delete(`/brand-icons/${encodeURIComponent(brand)}`);
      toast.success(`${brand} ikonu silindi`);
      fetchBrandIcons();
    } catch { toast.error('Silme hatası'); }
  };
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const handleBulkDelete = async () => {
    try {
      setBulkDeleting(true);
      const response = await api.post('/vehicles/bulk-delete', { vehicle_ids: Array.from(selectedIds) });
      if (response.data.success) {
        toast.success(response.data.message);
        setBulkDeleteConfirm(false);
        setSelectedIds(new Set());
        fetchVehicles();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('vehicles.bulkDeleteError'));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Get unique fuel types from data
  const fuelTypes = [...new Set(vehicles.map(v => v.fuel_type).filter(Boolean))];

  // Common colors
  const COLORS = ['Beyaz', 'Siyah', 'Gri', 'Kırmızı', 'Mavi', 'Yeşil', 'Sarı', 'Turuncu', 'Kahverengi', 'Gümüş'];

  // Send test notification
  const [sendingNotification, setSendingNotification] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useState(null);
  
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadExcelFile(file);
    if (e.target) e.target.value = '';
  };

  // Yardımcı: tek bir Excel dosyasını yükle (input change & Ctrl+V paste için ortak).
  const uploadExcelFile = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const response = await api.post('/vehicles/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchVehicles();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('vehicles.importError'));
    } finally {
      setUploading(false);
    }
  };

  // ─── Ctrl+V (Paste) → Excel içe aktarımı ──────────────────────────────────
  // Araçlar sayfasındayken Excel dosyasını pano üzerinden yapıştırınca
  // /vehicles/import endpoint'ine yüklenir. Sadece .xlsx/.xls kabul edilir;
  // başka tipler (image vb.) sessizce yoksayılır.
  usePasteUpload((files) => {
    const file = files[0];
    if (file) uploadExcelFile(file);
  }, { accept: '.xlsx,.xls' });
  
  const handleSendNotifications = async () => {
    try {
      setSendingNotification(true);
      const response = await api.post('/vehicles/check-notifications');
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('vehicles.notificationFailed'));
    } finally {
      setSendingNotification(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Car className="h-6 w-6 text-blue-600" />
            {t('vehicles.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">{t('vehicles.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && summary.with_warnings > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSendNotifications}
              disabled={sendingNotification}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {sendingNotification ? (
                <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">{t('vehicles.sendReminder')}</span>
            </Button>
          )}
          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleExcelUpload}
              disabled={uploading}
            />
            <Button variant="outline" size="sm" asChild disabled={uploading} data-testid="upload-vehicles-btn">
              <span>
                {uploading ? (
                  <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 sm:mr-2" />
                )}
                <span className="hidden sm:inline">{t('vehicles.excelUpload')}</span>
              </span>
            </Button>
          </label>
          {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && (
            <>
              <Button variant="outline" size="sm" onClick={() => setBrandIconsDialog(true)} data-testid="brand-icons-btn">
                <ImageIcon className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('vehicles.brandIcons')}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setVergCodesDialog(true)} data-testid="verg-codes-btn" className="border-teal-200 text-teal-700 hover:bg-teal-50">
                <FileCheck className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Vergunning Kodları</span>
              </Button>
              <Button variant="outline" size="sm" onClick={openVergReport} data-testid="verg-report-btn" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                <BarChart3 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Vergunning Raporu</span>
              </Button>
            </>
          )}
          <Button size="sm" onClick={openAddDialog} data-testid="add-vehicle-btn">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('vehicles.newVehicle')}</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder={t('vehicles.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="filter-search"
              />
            </div>

            {/* Firm */}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && (
              <Select value={selectedFirm || "all"} onValueChange={(v) => setSelectedFirm(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm w-auto min-w-[130px]" data-testid="filter-firm">
                  <Building2 className="h-3.5 w-3.5 mr-1.5 text-slate-400 shrink-0" />
                  <SelectValue placeholder={t('vehicles.allFirms')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('vehicles.allFirms')}</SelectItem>
                  {firms.map(firm => (
                    <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Filter popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 relative" data-testid="filter-advanced-btn">
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  {t('vehicles.filterBtn')}
                  {(() => {
                    const count = [fuelFilter, statusFilter, vergunningFilter, tireFilter].filter(Boolean).length + (includeSold ? 1 : 0);
                    return count > 0 ? (
                      <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold">
                        {count}
                      </span>
                    ) : null;
                  })()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-4" align="start">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{t('vehicles.filterTitle')}</p>
                <div className="space-y-3">
                  {/* Fuel */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">{t('vehicles.fuelTypeFilter')}</Label>
                    <Select value={fuelFilter || "all"} onValueChange={(v) => setFuelFilter(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-8 text-sm" data-testid="filter-fuel">
                        <SelectValue placeholder={t('vehicles.allFuel')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('vehicles.allFuel')}</SelectItem>
                        {fuelTypes.map(ft => (
                          <SelectItem key={ft} value={ft}>{ft}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Status */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">{t('vehicles.statusFilter')}</Label>
                    <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-8 text-sm" data-testid="filter-status">
                        <SelectValue placeholder={t('vehicles.allStatus')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('vehicles.allStatus')}</SelectItem>
                        <SelectItem value="ok">{t('vehicles.ok')}</SelectItem>
                        <SelectItem value="warning">{t('vehicles.warningStatus')}</SelectItem>
                        <SelectItem value="critical">{t('vehicles.criticalStatus')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Vergunning */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">{t('vehicles.vergunningFilter')}</Label>
                    <Select value={vergunningFilter || "all"} onValueChange={(v) => setVergunningFilter(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-8 text-sm" data-testid="filter-vergunning">
                        <SelectValue placeholder={t('vehicles.vergunningAll')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('vehicles.vergunningAll')}</SelectItem>
                        <SelectItem value="yes">{t('vehicles.vergunningYes')}</SelectItem>
                        <SelectItem value="no">{t('vehicles.vergunningNo')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Tire */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">{t('vehicles.tireFilter')}</Label>
                    <Select value={tireFilter || "all"} onValueChange={(v) => setTireFilter(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-8 text-sm" data-testid="filter-tire">
                        <SelectValue placeholder={t('vehicles.tireAll')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('vehicles.tireAll')}</SelectItem>
                        <SelectItem value="has">{t('vehicles.tireHas')}</SelectItem>
                        <SelectItem value="none">{t('vehicles.tireNone')}</SelectItem>
                        <SelectItem value="Y">{t('vehicles.tireSummer')}</SelectItem>
                        <SelectItem value="K">{t('vehicles.tireWinter')}</SelectItem>
                        <SelectItem value="YK">{t('vehicles.tireAllSeason')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Sold toggle */}
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600 dark:text-slate-400 pt-1">
                    <input
                      type="checkbox"
                      checked={includeSold}
                      onChange={(e) => setIncludeSold(e.target.checked)}
                      className="rounded border-slate-300"
                      data-testid="include-sold-toggle"
                    />
                    <Tag className="h-3.5 w-3.5" />
                    {t('vehicles.includeSold')}
                  </label>
                  {/* Clear */}
                  {(fuelFilter || statusFilter || vergunningFilter || tireFilter || includeSold) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 mt-1"
                      onClick={() => { setFuelFilter(''); setStatusFilter(''); setVergunningFilter(''); setTireFilter(''); setIncludeSold(false); }}
                      data-testid="filter-reset"
                    >
                      <X className="h-3 w-3 mr-1" />
                      {t('vehicles.clearAllFilters')}
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Active filter chips */}
            {(fuelFilter || statusFilter || vergunningFilter || tireFilter || brandFilter || includeSold) && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {brandFilter && (
                  <button onClick={() => setBrandFilter('')} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" data-testid="clear-brand-filter">
                    {t('vehicles.brand')}: {brandFilter} <X className="h-3 w-3" />
                  </button>
                )}
                {fuelFilter && (
                  <button onClick={() => setFuelFilter('')} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                    {t('vehicles.fuelTypeFilter')}: {fuelFilter} <X className="h-3 w-3" />
                  </button>
                )}
                {statusFilter && (
                  <button onClick={() => setStatusFilter('')} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
                    {t('vehicles.statusFilter')}: {statusFilter === 'ok' ? t('vehicles.ok') : statusFilter === 'warning' ? t('vehicles.warningStatus') : t('vehicles.criticalStatus')} <X className="h-3 w-3" />
                  </button>
                )}
                {vergunningFilter && (
                  <button onClick={() => setVergunningFilter('')} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors">
                    {t('vehicles.vergunningFilter')}: {vergunningFilter === 'yes' ? t('vehicles.vergunningYes') : t('vehicles.vergunningNo')} <X className="h-3 w-3" />
                  </button>
                )}
                {tireFilter && (
                  <button onClick={() => setTireFilter('')} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors">
                    {t('vehicles.tireFilter')}: {tireFilter === 'has' ? t('vehicles.tireHas') : tireFilter === 'none' ? t('vehicles.tireNone') : tireFilter === 'Y' ? t('vehicles.tireSummer') : tireFilter === 'K' ? t('vehicles.tireWinter') : t('vehicles.tireAllSeason')} <X className="h-3 w-3" />
                  </button>
                )}
                {includeSold && (
                  <button onClick={() => setIncludeSold(false)} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                    {t('vehicles.includeSold')} <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('vehicles.totalVehicles')}</p>
                <p className="text-2xl font-bold">{vehicles.length}</p>
              </div>
              <Car className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('vehicles.firmCount')}</p>
                  <p className="text-2xl font-bold">{new Set(vehicles.map(v => v.firm_id)).size}</p>
                </div>
                <Building2 className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Warning Cards */}
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'warning' ? 'ring-2 ring-amber-400' : ''} ${summary.with_warnings > 0 ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 hover:shadow-md' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'warning' ? 'all' : 'warning')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">{t('vehicles.hasWarnings')}</p>
                <p className="text-2xl font-bold text-amber-700">{summary.with_warnings}</p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${summary.with_warnings > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'critical' ? 'ring-2 ring-red-400' : ''} ${summary.critical > 0 ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200 hover:shadow-md' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'critical' ? 'all' : 'critical')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">{t('vehicles.hasCritical')}</p>
                <p className="text-2xl font-bold text-red-700">{summary.critical}</p>
              </div>
              <AlertCircle className={`h-8 w-8 ${summary.critical > 0 ? 'text-red-500' : 'text-slate-300'}`} />
            </div>
          </CardContent>
        </Card>
      </div>
      <FleetStatusCards summary={summary} />

      {/* Charts - Collapsible */}
      {showChart && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-200">
          {/* Brand Distribution Chart */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-500" />
                {t('vehicles.brandDistribution')}
                {brandFilter && (
                  <button onClick={() => setBrandFilter('')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors ml-2">
                    {brandFilter} <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowChart(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="pb-4">
              {(() => {
                const brandCounts = {};
                vehicles.forEach(v => {
                  if (fuelFilter && v.fuel_type !== fuelFilter) return;
                  if (statusFilter === 'critical' && !v.has_critical) return;
                  if (statusFilter === 'warning' && !v.has_warnings) return;
                  if (statusFilter === 'ok' && (v.has_warnings || v.has_critical)) return;
                  const b = v.brand?.trim() || t('vehicles.unknownBrand');
                  brandCounts[b] = (brandCounts[b] || 0) + 1;
                });
                const sorted = Object.entries(brandCounts).sort((a, b) => b[1] - a[1]);
                const total = sorted.reduce((sum, [, c]) => sum + c, 0);
                const max = sorted[0]?.[1] || 1;
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {sorted.map(([brand, count]) => {
                      const style = getBrandStyle(brand);
                      const pct = Math.round((count / total) * 100);
                      const isActive = brandFilter === brand;
                      return (
                        <div 
                          key={brand} 
                          className={cn("flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all duration-200", isActive ? "bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-700" : "bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50")}
                          onClick={() => setBrandFilter(isActive ? '' : brand)}
                          data-testid={`brand-filter-${brand}`}
                        >                          {getBrandIconUrl(brand) ? (
                            <img src={getBrandIconUrl(brand)} alt={brand} title={brand} className="w-8 h-8 rounded-full object-contain bg-white border border-slate-200 shrink-0" />
                          ) : (
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold shrink-0 ${style.color}`} title={brand}>
                              {(() => { const Logo = getBrandLogoComponent(brand); return <Logo className="w-5 h-5" />; })()}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{brand}</span>
                              <span className="text-xs font-bold text-slate-500 ml-2">{count} ({pct}%)</span>
                            </div>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                              <div
                                className="h-2 rounded-full transition-all duration-500"
                                style={{
                                  width: `${(count / max) * 100}%`,
                                  background: `hsl(${(brand.charCodeAt(0) * 37) % 360}, 65%, 55%)`
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Fuel Type Distribution Chart */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Fuel className="h-5 w-5 text-amber-500" />
                {t('vehicles.fuelDistribution')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              {(() => {
                const fuelCounts = {};
                filteredVehicles.forEach(v => {
                  const ft = v.fuel_type?.trim() || t('vehicles.notSpecified');
                  fuelCounts[ft] = (fuelCounts[ft] || 0) + 1;
                });
                const chartData = Object.entries(fuelCounts)
                  .map(([name, value]) => ({ name, value, pct: Math.round((value / filteredVehicles.length) * 100) }))
                  .sort((a, b) => b.value - a.value);
                const FUEL_COLORS = {
                  'DIESEL': '#64748b',
                  'BENZINE': '#3b82f6',
                  'RODE_MAZOUT': '#ef4444',
                  'YOK': '#d1d5db',
                  'EV': '#22c55e',
                  'HYBRID': '#06b6d4',
                  'LPG': '#f59e0b',
                  'CNG': '#8b5cf6',
                  [t('vehicles.notSpecified')]: '#e2e8f0',
                };
                const getColor = (name) => FUEL_COLORS[name] || `hsl(${(name.charCodeAt(0) * 47) % 360}, 55%, 55%)`;
                if (filteredVehicles.length === 0) {
                  return <div className="h-[260px] flex items-center justify-center text-slate-400">{t('vehicles.noDataFound')}</div>;
                }
                return (
                  <div className="flex flex-col items-center">
                    <div className="h-[220px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={85}
                            paddingAngle={3}
                            dataKey="value"
                            stroke="none"
                          >
                            {chartData.map((entry) => (
                              <Cell key={entry.name} fill={getColor(entry.name)} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (active && payload?.[0]) {
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-white dark:bg-slate-800 px-3 py-2 rounded-lg shadow-lg border text-sm">
                                    <span className="font-semibold">{d.name}</span>: {d.value} {t('vehicles.vehicleCount')} ({d.pct}%)
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend
                            formatter={(value) => <span className="text-xs text-slate-600 dark:text-slate-300">{value}</span>}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="w-full grid grid-cols-2 gap-2 mt-2">
                      {chartData.map((item) => (
                        <div key={item.name} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: getColor(item.name) }} />
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{item.name}</span>
                          <span className="ml-auto text-xs font-bold text-slate-500 whitespace-nowrap">{item.value} ({item.pct}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Vehicles Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle>{t('vehicles.vehicleList')}</CardTitle>
            <span className="text-sm text-slate-500">
              {filteredVehicles.length} {t('vehicles.vehicleCount')} {filteredVehicles.length !== vehicles.length && `/ ${vehicles.length} ${t('vehicles.totalLabel')}`}
            </span>
            {brandFilter && (
              <button onClick={() => setBrandFilter('')} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 transition-colors" data-testid="clear-brand-filter">
                Marka: {brandFilter} <X className="h-3 w-3" />
              </button>
            )}
            <Button 
              variant={showChart ? "default" : "outline"} 
              size="sm" 
              onClick={() => setShowChart(!showChart)}
              data-testid="toggle-chart-btn"
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              {showChart ? t('vehicles.hideChart') : t('vehicles.showBrandChart')}
            </Button>
            {/* Column visibility toggle */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="column-settings-btn">
                  <Columns3 className="h-4 w-4 mr-1" />
                  {t('vehicles.columns')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="end">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-500 mb-2">{t('vehicles.visibleColumns')}</p>
                  {ALL_COLUMNS.filter(c => {
                    if (c.adminOnly && user?.role !== 'SUPER_ADMIN' && user?.role !== 'FULL_ADMIN') return false;
                    return true;
                  }).map(col => (
                    <label
                      key={col.key}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 ${col.alwaysVisible ? 'opacity-60' : ''}`}
                      data-testid={`col-toggle-${col.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        disabled={col.alwaysVisible}
                        className="rounded border-slate-300"
                      />
                      <span className="text-slate-700 dark:text-slate-300">{col.label}</span>
                    </label>
                  ))}
                  <div className="border-t pt-2 mt-2">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      onClick={() => {
                        const defaults = getDefaultColumns();
                        setVisibleColumns(defaults);
                        localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(defaults));
                      }}
                      data-testid="reset-columns-btn"
                    >
                      {t('vehicles.showAll')}
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {/* Bulk delete bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {selectedIds.size} {t('vehicles.selectedCount')}
              </span>
              {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setBulkVergDialog(true); setBulkVergSelected([]); setBulkVergMode('add'); }}
                  data-testid="bulk-verg-btn"
                  className="border-teal-200 text-teal-700 hover:bg-teal-50"
                >
                  <FileCheck className="h-4 w-4 mr-1" />
                  Vergunning Toplu Ata
                </Button>
              )}
              {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkAssignDialog(true)}
                  data-testid="bulk-assign-btn"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  Toplu Şoför Ata
                </Button>
              )}
              {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setBulkTransDialog(true); setBulkTransValue('AUTOMATIC'); }}
                  data-testid="bulk-trans-btn"
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                >
                  <TransmissionBadge value="AUTOMATIC" size={14} />
                  <span className="ml-1">Toplu Vites</span>
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteConfirm(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {t('vehicles.deleteSelected')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              <Car className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>{t('vehicles.noVehicles')}</p>
              <Button variant="outline" className="mt-4" onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                {t('vehicles.addFirst')}
              </Button>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={paginatedVehicles.length > 0 && selectedIds.size === paginatedVehicles.length}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    {isColumnVisible('status') && <TableHead className="w-8 px-1 text-center">S</TableHead>}
                    {isColumnVisible('plate') && (
                      <SortableHeader field="license_plate" label={t('vehicles.tablePlate')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('firm') && (
                      <SortableHeader field="firm_name" label={t('vehicles.tableFirm')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('brandModel') && (
                      <SortableHeader field="brand" label={t('vehicles.tableBrandModel')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('year') && (
                      <SortableHeader field="year" label={t('vehicles.tableYear')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('transmission') && (
                      <SortableHeader field="transmission" label="Vites" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('fuel') && (
                      <SortableHeader field="fuel_type" label={t('vehicles.tableFuel')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('tire') && (
                      <SortableHeader field="tire_type" label="Lastik" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('vergunning') && (
                      <SortableHeader field="vergunning_number" label="Verg." sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('insurance') && (
                      <SortableHeader field="insurance_expiry_date" label={t('vehicles.tableInsurance')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('inspection') && (
                      <SortableHeader field="inspection_expiry_date" label={t('vehicles.tableInspection')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    {isColumnVisible('maintenance') && (
                      <SortableHeader field="next_maintenance_date" label={t('vehicles.tableNextMaintenance')} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    )}
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedVehicles.map((vehicle) => {
                    const isHighlighted = lastVisitedId && vehicle.id === lastVisitedId;
                    return (
                    <TableRow 
                      key={vehicle.id} 
                      data-row-id={vehicle.id}
                      className={`cursor-pointer transition-all duration-500 ${vehicle.has_critical ? 'bg-red-50' : vehicle.has_warnings ? 'bg-amber-50' : ''} ${selectedIds.has(vehicle.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''} ${isHighlighted ? 'ring-2 ring-inset ring-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-blue-50/70 dark:hover:bg-blue-900/10'}`}
                      onClick={(e) => {
                        if (e.target.closest('button, input, label, a, [role="menuitem"]')) return;
                        handleNavigateVehicle(vehicle);
                      }}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(vehicle.id)}
                          onChange={() => toggleSelect(vehicle.id)}
                          className="rounded border-slate-300"
                          data-testid={`select-vehicle-${vehicle.id}`}
                        />
                      </TableCell>
                      {isColumnVisible('status') && (
                      <TableCell>
                        <TooltipProvider>
                          {vehicle.warnings?.length > 0 ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <div className="flex gap-1">
                                  {vehicle.has_critical ? (
                                    <AlertCircle className="h-5 w-5 text-red-500" />
                                  ) : (
                                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                <div className="space-y-1">
                                  {vehicle.warnings.map((w, i) => (
                                    <p key={i} className={w.level === 'critical' ? 'text-red-600' : 'text-amber-600'}>
                                      {w.type === 'insurance' && <Shield className="h-3 w-3 inline mr-1" />}
                                      {w.type === 'inspection' && <FileCheck className="h-3 w-3 inline mr-1" />}
                                      {w.type === 'maintenance' && <Wrench className="h-3 w-3 inline mr-1" />}
                                      {w.message}
                                    </p>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-green-500">✓</span>
                          )}
                        </TooltipProvider>
                      </TableCell>
                      )}
                      {isColumnVisible('plate') && (
                      <TableCell className="font-bold">
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={`font-mono text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${vehicle.sale_date ? 'opacity-50 line-through' : ''}`}
                            onClick={() => handleNavigateVehicle(vehicle)}
                            data-testid={`vehicle-plate-${vehicle.id}`}
                          >
                            {vehicle.license_plate}
                          </Badge>
                          {vehicle.sale_date && (
                            <Badge variant="secondary" className="text-[10px] bg-slate-200 text-slate-600">{t('common.sold')}</Badge>
                          )}
                        </div>
                      </TableCell>
                      )}
                      {isColumnVisible('firm') && (
                        <TableCell>
                          {vehicle.firm_name ? (
                            <Badge className={`font-medium border ${getFirmColor(vehicle.firm_name)}`}>
                              <Building2 className="h-3 w-3 mr-1" />
                              {vehicle.firm_name}
                            </Badge>
                          ) : <span className="text-slate-400">-</span>}
                        </TableCell>
                      )}
                      {isColumnVisible('brandModel') && (
                      <TableCell>
                        {vehicle.brand ? (
                          <div className="flex items-center gap-2">
                            {getBrandIconUrl(vehicle.brand) ? (
                              <img src={getBrandIconUrl(vehicle.brand)} alt={vehicle.brand} title={vehicle.brand} className="w-8 h-8 rounded-full object-contain bg-white border border-slate-200" />
                            ) : (
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${getBrandStyle(vehicle.brand).color}`} title={vehicle.brand}>
                                {(() => { const Logo = getBrandLogoComponent(vehicle.brand); return <Logo className="w-5 h-5" />; })()}
                              </span>
                            )}
                            <div>
                              {vehicle.model && <span className="text-sm text-slate-600 dark:text-slate-300">{vehicle.model}</span>}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      )}
                      {isColumnVisible('year') && <TableCell>{vehicle.year || '-'}</TableCell>}
                      {isColumnVisible('transmission') && (
                        <TableCell>
                          <TransmissionBadge value={vehicle.transmission} />
                        </TableCell>
                      )}
                      {isColumnVisible('fuel') && (
                      <TableCell>
                        <FuelBadge value={vehicle.fuel_type} />
                      </TableCell>
                      )}
                      {isColumnVisible('tire') && (
                      <TableCell>
                        {vehicle.tire_type ? (
                          <div className="flex flex-col">
                            <Badge variant="outline" className={
                              vehicle.tire_type === 'Y' ? 'border-amber-300 text-amber-700' :
                              vehicle.tire_type === 'K' ? 'border-sky-300 text-sky-700' :
                              'border-violet-300 text-violet-700'
                            }>{vehicle.tire_type === 'Y' ? 'Yazlık' : vehicle.tire_type === 'K' ? 'Kışlık' : 'Y-K'}</Badge>
                            {vehicle.tire_km && <span className="text-[10px] text-slate-400 mt-0.5">{Number(vehicle.tire_km).toLocaleString()} km</span>}
                          </div>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      )}
                      {isColumnVisible('vergunning') && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN') ? (
                          <div className="flex flex-wrap gap-0.5 items-center min-h-[22px]">
                            {(vehicle.vergunning_codes || []).map(code => {
                              const t = (vehicle.vergunning_types || {})[code];
                              return (
                                <Popover key={code}>
                                  <PopoverTrigger asChild>
                                    <button
                                      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded border transition hover:brightness-95 ${t === 'COPY' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
                                      title={`${code} — O/K değiştir veya kaldır`}
                                      data-testid={`verg-chip-${vehicle.license_plate}-${code}`}
                                    >
                                      {code}
                                      {t && (
                                        <span className={`text-[8px] px-0.5 rounded font-bold ${t === 'COPY' ? 'bg-rose-600 text-white' : 'bg-emerald-600 text-white'}`}>
                                          {t === 'COPY' ? 'K' : 'O'}
                                        </span>
                                      )}
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-56 p-3" align="start">
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="font-mono font-semibold text-sm">{code}</span>
                                        <button
                                          type="button"
                                          className="text-[11px] text-rose-600 hover:underline"
                                          onClick={() => {
                                            const next = (vehicle.vergunning_codes || []).filter(c => c !== code);
                                            const nextTypes = { ...(vehicle.vergunning_types || {}) };
                                            delete nextTypes[code];
                                            updateVehicleVergCodes(vehicle.id, next, nextTypes);
                                          }}
                                          data-testid={`verg-chip-remove-${vehicle.license_plate}-${code}`}
                                        >
                                          Kaldır
                                        </button>
                                      </div>
                                      <div className="text-[11px] text-slate-500">Tip seçin:</div>
                                      <div className="grid grid-cols-2 gap-1">
                                        <button
                                          type="button"
                                          className={`h-8 rounded text-xs font-semibold border ${(t || 'ORIGINAL') === 'ORIGINAL' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                          onClick={() => {
                                            const current = vehicle.vergunning_codes || [];
                                            const nextTypes = { ...(vehicle.vergunning_types || {}), [code]: 'ORIGINAL' };
                                            updateVehicleVergCodes(vehicle.id, current, nextTypes);
                                          }}
                                          data-testid={`veh-verg-orig-${vehicle.id}-${code}`}
                                        >
                                          Orijinal (O)
                                        </button>
                                        <button
                                          type="button"
                                          className={`h-8 rounded text-xs font-semibold border ${t === 'COPY' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                          onClick={() => {
                                            const current = vehicle.vergunning_codes || [];
                                            const nextTypes = { ...(vehicle.vergunning_types || {}), [code]: 'COPY' };
                                            updateVehicleVergCodes(vehicle.id, current, nextTypes);
                                          }}
                                          data-testid={`veh-verg-copy-${vehicle.id}-${code}`}
                                        >
                                          Kopya (K)
                                        </button>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              );
                            })}
                            {/* Kod ekleme butonu — tam liste burada açılır */}
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  className="inline-flex items-center justify-center w-5 h-5 rounded border border-dashed border-slate-300 text-slate-400 hover:text-teal-600 hover:border-teal-400 text-xs"
                                  title="Vergunning kodu ekle"
                                  data-testid={`verg-add-${vehicle.license_plate}`}
                                >
                                  +
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-0" align="start">
                                {vergCodes.length === 0 ? (
                                  <p className="text-xs text-slate-400 p-4">Henüz kod eklenmemiş. Önce "Vergunning Kodları" butonundan ekleyin.</p>
                                ) : (
                                  <Command filter={(value, search) => {
                                    if (!search) return 1;
                                    return turkishIncludes(value, search) ? 1 : 0;
                                  }}>
                                    <CommandInput placeholder="Kod veya firma ara..." className="h-9" />
                                    <CommandList className="max-h-72">
                                      <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                                      {firms.map(firm => {
                                        const firmCodes = vergCodes.filter(c => c.firm_id === firm.id);
                                        if (firmCodes.length === 0) return null;
                                        const current = vehicle.vergunning_codes || [];
                                        const currentTypes = vehicle.vergunning_types || {};
                                        return (
                                          <CommandGroup key={firm.id} heading={firm.name}>
                                            {firmCodes.map(vc => {
                                              const active = current.includes(vc.code);
                                              return (
                                                <CommandItem
                                                  key={vc.id}
                                                  value={`${vc.code} ${firm.name}`}
                                                  onSelect={() => {
                                                    const next = active ? current.filter(c => c !== vc.code) : [...current, vc.code];
                                                    const nextTypes = { ...currentTypes };
                                                    if (active) delete nextTypes[vc.code];
                                                    else if (!nextTypes[vc.code]) nextTypes[vc.code] = 'ORIGINAL';
                                                    updateVehicleVergCodes(vehicle.id, next, nextTypes);
                                                  }}
                                                  className="cursor-pointer"
                                                >
                                                  <div className={`flex h-4 w-4 items-center justify-center rounded border ${active ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}>
                                                    {active && <Check className="h-3 w-3" />}
                                                  </div>
                                                  <span className="font-mono text-sm ml-2">{vc.code}</span>
                                                </CommandItem>
                                              );
                                            })}
                                          </CommandGroup>
                                        );
                                      })}
                                    </CommandList>
                                  </Command>
                                )}
                              </PopoverContent>
                            </Popover>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-0.5 items-center">
                            {(vehicle.vergunning_codes || []).length > 0 ? (
                              (vehicle.vergunning_codes || []).map(code => (
                                <span key={code} className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">
                                  {code}
                                </span>
                              ))
                            ) : (
                              <span className="text-slate-300 text-xs">-</span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      )}
                      {isColumnVisible('insurance') && (
                      <TableCell>
                        {vehicle.insurance_expiry_date ? (
                          <span className={`text-xs ${
                            vehicle.warnings?.some(w => w.type === 'insurance' && w.level === 'critical') ? 'text-red-600 font-semibold' :
                            vehicle.warnings?.some(w => w.type === 'insurance') ? 'text-amber-600' : 'text-slate-600 dark:text-slate-300'
                          }`}>
                            {new Date(vehicle.insurance_expiry_date).toLocaleDateString('tr-TR')}
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      )}
                      {isColumnVisible('inspection') && (
                      <TableCell>
                        {vehicle.inspection_expiry_date ? (
                          <span className={`text-xs ${
                            vehicle.warnings?.some(w => w.type === 'inspection' && w.level === 'critical') ? 'text-red-600 font-semibold' :
                            vehicle.warnings?.some(w => w.type === 'inspection') ? 'text-amber-600' : 'text-slate-600 dark:text-slate-300'
                          }`}>
                            {new Date(vehicle.inspection_expiry_date).toLocaleDateString('tr-TR')}
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      )}
                      {isColumnVisible('maintenance') && (
                      <TableCell>
                        {vehicle.next_maintenance_date ? (
                          <span className={`text-xs ${
                            vehicle.warnings?.some(w => w.type === 'maintenance' && w.level === 'critical') ? 'text-red-600 font-semibold' :
                            vehicle.warnings?.some(w => w.type === 'maintenance') ? 'text-amber-600' : 'text-slate-600 dark:text-slate-300'
                          }`}>
                            {new Date(vehicle.next_maintenance_date).toLocaleDateString('tr-TR')}
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      )}
                      <TableCell className="px-1">
                        <div className="flex gap-0">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => handleNavigateVehicle(vehicle)}
                                  data-testid={`vehicle-detail-${vehicle.id}`}
                                >
                                  <ExternalLink className="h-3.5 w-3.5 text-blue-500" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t('vehicles.vehicleDetailTitle')}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className="h-7 w-7 relative"
                                  onClick={() => setDocumentsVehicle(vehicle)}
                                  data-testid={`vehicle-docs-${vehicle.id}`}
                                >
                                  <FileText className="h-3.5 w-3.5 text-blue-500" />
                                  {vehicle.doc_count > 0 && (
                                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                                      {vehicle.doc_count}
                                    </span>
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t('vehicles.documentsTitle')}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 relative"
                                  onClick={() => setAssignDialogVehicle(vehicle)}
                                  data-testid={`vehicle-assign-${vehicle.id}`}
                                >
                                  <UserPlus className={`h-3.5 w-3.5 ${vehicle.current_driver_id ? 'text-emerald-600' : 'text-slate-500'}`} />
                                  {vehicle.current_driver_id && (
                                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-emerald-500" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {vehicle.current_driver_name
                                  ? `Atanan: ${vehicle.current_driver_name}`
                                  : 'Plaka ata'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditDialog(vehicle)}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDeleteConfirm(vehicle)}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t mt-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-16 h-8" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm whitespace-nowrap">
                  {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, filteredVehicles.length)} / {filteredVehicles.length}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === 1} onClick={() => setCurrentPage(1)} data-testid="page-first">
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="page-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 text-sm font-medium whitespace-nowrap">{currentPage} / {totalPages || 1}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="page-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)} data-testid="page-last">
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-600" />
              {editingVehicle ? t('vehicles.editVehicle') : t('vehicles.addVehicle')}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Firma */}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && (
              <div>
                <Label>{t('common.firm')} *</Label>
                <Select 
                  value={formData.firm_id} 
                  onValueChange={(v) => setFormData({...formData, firm_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('vehicles.selectFirm')} />
                  </SelectTrigger>
                  <SelectContent>
                    {firms.map(firm => (
                      <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Plaka */}
            <div>
              <Label>{t('vehicles.tablePlate')} *</Label>
              <Input
                placeholder={t('vehicles.platePlaceholder')}
                value={formData.license_plate}
                onChange={(e) => setFormData({...formData, license_plate: e.target.value.toUpperCase()})}
                className="font-mono"
                data-testid="vehicle-form-plate-input"
              />
            </div>

            {/* Marka / Model */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('vehicles.brand')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between font-normal"
                      data-testid="veh-brand-picker"
                    >
                      <span className="flex items-center gap-2 truncate">
                        {formData.brand ? (
                          <>
                            {brandIcons[formData.brand.trim().toUpperCase()] && (
                              <img
                                src={`${API_URL}/api/brand-icons/${encodeURIComponent(formData.brand.trim().toUpperCase())}/image`}
                                alt=""
                                className="h-5 w-5 object-contain"
                              />
                            )}
                            <span className="truncate">{formData.brand}</span>
                          </>
                        ) : (
                          <span className="text-slate-400">{t('vehicles.brandPlaceholder')}</span>
                        )}
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50 rotate-90" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command filter={(value, search) => turkishIncludes(value, search) ? 1 : 0}>
                      <CommandInput placeholder="Marka ara..." className="h-9" />
                      <CommandList className="max-h-72">
                        <CommandEmpty>
                          <div className="py-3 px-2 text-center space-y-2">
                            <p className="text-xs text-slate-500">Marka bulunamadı.</p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => { setAddDialog(false); setBrandIconsDialog(true); }}
                              data-testid="veh-brand-add-new"
                            >
                              Yeni marka ekle
                            </Button>
                          </div>
                        </CommandEmpty>
                        <CommandGroup heading="Kayıtlı Markalar">
                          {Object.keys(brandIcons).sort().map(b => (
                            <CommandItem
                              key={b}
                              value={b}
                              onSelect={() => setFormData({ ...formData, brand: b })}
                              className="cursor-pointer"
                              data-testid={`veh-brand-opt-${b}`}
                            >
                              <img
                                src={`${API_URL}/api/brand-icons/${encodeURIComponent(b)}/image`}
                                alt=""
                                className="h-5 w-5 object-contain mr-2"
                              />
                              <span>{b}</span>
                              {formData.brand?.trim().toUpperCase() === b && (
                                <Check className="ml-auto h-4 w-4 text-teal-600" />
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        <div className="border-t px-2 py-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start text-teal-600 hover:text-teal-700"
                            onClick={() => { setAddDialog(false); setBrandIconsDialog(true); }}
                            data-testid="veh-brand-manage"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Marka ikonları yönetimi...
                          </Button>
                        </div>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>{t('vehicles.model')}</Label>
                <Input
                  placeholder={t('vehicles.modelPlaceholder')}
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                />
              </div>
            </div>

            {/* Yıl / Renk */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('vehicles.buildYear')}</Label>
                <Input
                  type="number"
                  placeholder={t('vehicles.yearPlaceholder')}
                  value={formData.year}
                  onChange={(e) => setFormData({...formData, year: e.target.value})}
                  min={1990}
                  max={new Date().getFullYear() + 1}
                />
              </div>
              <div>
                <Label>{t('vehicles.color')}</Label>
                <Select 
                  value={formData.color} 
                  onValueChange={(v) => setFormData({...formData, color: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('vehicles.selectColor')} />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORS.map(color => (
                      <SelectItem key={color} value={color}>{color}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Araç Detayları */}
            <div className="border-t pt-4 mt-2">
              <h4 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2 mb-3">
                <Car className="h-4 w-4" />
                {t('vehicles.vehicleDetailsSection')}
              </h4>
            </div>

            {/* Chassisnummer / Laadvermogen */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Chassisnummer</Label>
                <Input
                  placeholder={t('vehicles.chassisPlaceholder')}
                  value={formData.chassis_number}
                  onChange={(e) => setFormData({...formData, chassis_number: e.target.value.toUpperCase()})}
                  className="font-mono text-xs"
                />
              </div>
              <div>
                <Label>Laadvermogen</Label>
                <Input
                  placeholder={t('vehicles.loadPlaceholder')}
                  value={formData.load_capacity}
                  onChange={(e) => setFormData({...formData, load_capacity: e.target.value})}
                />
              </div>
            </div>

            {/* Brandstof / Vergunningnummer */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Fuel className="h-3 w-3" />
                  Brandstof
                </Label>
                <Select 
                  value={formData.fuel_type || "none"} 
                  onValueChange={(v) => setFormData({...formData, fuel_type: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('vehicles.fuelSelectLabel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('vehicles.selectOption')}</SelectItem>
                    <SelectItem value="DIESEL">Diesel</SelectItem>
                    <SelectItem value="BENZINE">Benzine</SelectItem>
                    <SelectItem value="RODE_MAZOUT">Rode Mazout</SelectItem>
                    <SelectItem value="YOK">Yok</SelectItem>
                    <SelectItem value="EV">EV (Elektrik)</SelectItem>
                    <SelectItem value="HYBRID">Hybrid</SelectItem>
                    <SelectItem value="LPG">LPG</SelectItem>
                    <SelectItem value="CNG">CNG</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vites</Label>
                <Select
                  value={formData.transmission || "none"}
                  onValueChange={(v) => setFormData({ ...formData, transmission: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="veh-form-transmission">
                    <SelectValue placeholder="Seçiniz..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    <SelectItem value="AUTOMATIC">Otomatik (O)</SelectItem>
                    <SelectItem value="MANUAL">Manuel (M)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <KeyRound className="h-3 w-3" />
                  Vergunningnummer
                </Label>
                <Input
                  placeholder={t('vehicles.permitPlaceholder')}
                  value={formData.permit_number}
                  onChange={(e) => setFormData({...formData, permit_number: e.target.value})}
                />
              </div>
            </div>

            {/* Sticker / Lease of Eigendom */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sticker (Ja/Nee)</Label>
                <Select 
                  value={formData.sticker || "none"} 
                  onValueChange={(v) => setFormData({...formData, sticker: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('vehicles.selectOption')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('vehicles.selectOption')}</SelectItem>
                    <SelectItem value="Ja">Ja</SelectItem>
                    <SelectItem value="Nee">Nee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lease of Eigendom</Label>
                <Select 
                  value={formData.ownership_type || "none"} 
                  onValueChange={(v) => setFormData({...formData, ownership_type: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('vehicles.ownershipPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('vehicles.selectOption')}</SelectItem>
                    <SelectItem value="Eigendom">Eigendom</SelectItem>
                    <SelectItem value="Lease (J&T)">Lease (J&T)</SelectItem>
                    <SelectItem value="Lease (Arval)">Lease (Arval)</SelectItem>
                    <SelectItem value="Lease">Lease</SelectItem>
                    <SelectItem value="Kiralık">Kiralık</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.ownership_type === 'Kiralık' && (
              <RentalFields formData={formData} setFormData={setFormData} vehicles={vehicles} />
            )}

            {/* Plaats Aankoop / Aankoopdatum */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Plaats Aankoop
                </Label>
                <Input
                  placeholder={t('vehicles.purchasePlaceholder')}
                  value={formData.purchase_location}
                  onChange={(e) => setFormData({...formData, purchase_location: e.target.value})}
                />
              </div>
              <div>
                <Label>Aankoopdatum</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                />
              </div>
            </div>

            {/* Satış Tarihi */}
            <div>
              <Label className="flex items-center gap-1">
                <Tag className="h-3 w-3" />
                {t('common.saleDate')}
                {formData.sale_date && <Badge variant="secondary" className="ml-2 text-[10px]">{t('common.sold')}</Badge>}
              </Label>
              <Input
                type="date"
                value={formData.sale_date}
                onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">{t('common.saleDateHint')}</p>
            </div>

            {/* Verzekeringsmaatschappij */}
            <div>
              <Label className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Verzekeringsmaatschappij
              </Label>
              <Input
                placeholder={t('vehicles.insuranceCompanyLabel')}
                value={formData.insurance_company}
                onChange={(e) => setFormData({...formData, insurance_company: e.target.value})}
              />
            </div>

            {/* Bakım ve Sigorta Başlığı */}
            <div className="border-t pt-4 mt-2">
              <h4 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2 mb-3">
                <Wrench className="h-4 w-4" />
                {t('vehicles.maintenanceTracking')}
              </h4>
            </div>

            {/* Sigorta / Muayene */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  {t('vehicles.insuranceExpiryDate')}
                </Label>
                <Input
                  type="date"
                  value={formData.insurance_expiry_date}
                  onChange={(e) => setFormData({...formData, insurance_expiry_date: e.target.value})}
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <FileCheck className="h-3 w-3" />
                  {t('vehicles.inspectionExpiryDate')}
                </Label>
                <Input
                  type="date"
                  value={formData.inspection_expiry_date}
                  onChange={(e) => setFormData({...formData, inspection_expiry_date: e.target.value})}
                />
              </div>
            </div>

            {/* Bakım Tarihleri */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t('vehicles.lastMaintenanceDate')}
                </Label>
                <Input
                  type="date"
                  value={formData.last_maintenance_date}
                  onChange={(e) => setFormData({...formData, last_maintenance_date: e.target.value})}
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t('vehicles.nextMaintenanceDate')}
                </Label>
                <Input
                  type="date"
                  value={formData.next_maintenance_date}
                  onChange={(e) => setFormData({...formData, next_maintenance_date: e.target.value})}
                />
              </div>
            </div>

            {/* Bakım KM */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('vehicles.lastMaintenanceKmLabel')}</Label>
                <Input
                  type="number"
                  placeholder={t('vehicles.maintenanceKmPlaceholder')}
                  value={formData.maintenance_km}
                  onChange={(e) => setFormData({...formData, maintenance_km: e.target.value})}
                />
              </div>
              <div>
                <Label>{t('vehicles.nextMaintenanceKmLabel')}</Label>
                <Input
                  type="number"
                  placeholder={t('vehicles.nextMaintenanceKmPlaceholder')}
                  value={formData.next_maintenance_km}
                  onChange={(e) => setFormData({...formData, next_maintenance_km: e.target.value})}
                />
              </div>
            </div>

            {/* Lastik (Band) */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  Lastik Tipi
                </Label>
                <Select value={formData.tire_type} onValueChange={(v) => setFormData({...formData, tire_type: v, tire_date: v ? (formData.tire_date || new Date().toISOString().split('T')[0]) : '', tire_km: v ? formData.tire_km : ''})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Yok</SelectItem>
                    <SelectItem value="Y">Yazlık (Y)</SelectItem>
                    <SelectItem value="K">Kışlık (K)</SelectItem>
                    <SelectItem value="YK">Yazlık-Kışlık (YK)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.tire_type && formData.tire_type !== 'NONE' && (
                <>
                  <div>
                    <Label>Lastik KM</Label>
                    <Input
                      type="number"
                      placeholder="Kilometre"
                      value={formData.tire_km}
                      onChange={(e) => setFormData({...formData, tire_km: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Lastik Tarihi</Label>
                    <Input
                      type="date"
                      value={formData.tire_date}
                      onChange={(e) => setFormData({...formData, tire_date: e.target.value})}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Notlar */}
            <div>
              <Label>{t('vehicles.notesLabel')}</Label>
              <Input
                placeholder={t('vehicles.notesPlaceholder')}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="vehicle-form-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingVehicle ? t('customers.update') : t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('vehicles.deleteVehicleTitle')}</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            <Badge variant="outline" className="font-mono mr-2">{deleteConfirm?.license_plate}</Badge>
            {t('vehicles.deleteVehicleConfirm')}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)}>
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle Documents Dialog */}
      <Dialog open={!!documentsVehicle} onOpenChange={() => setDocumentsVehicle(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {documentsVehicle && (
            <VehicleDocuments
              vehicleId={documentsVehicle.id}
              vehiclePlate={documentsVehicle.license_plate}
              vehicleFirmId={documentsVehicle.firm_id}
              assignedVergunningCodes={documentsVehicle.vergunning_codes || []}
              onClose={() => setDocumentsVehicle(null)}
              onChanged={fetchVehicles}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Vehicle Assignment Dialog */}
      <VehicleAssignDialog
        open={!!assignDialogVehicle}
        vehicle={assignDialogVehicle}
        onClose={() => setAssignDialogVehicle(null)}
        onAssigned={() => fetchVehicles()}
      />

      {/* Bulk Vehicle Assignment Dialog */}
      <BulkVehicleAssignDialog
        open={bulkAssignDialog}
        vehicles={vehicles.filter((v) => selectedIds.has(v.id))}
        onClose={() => setBulkAssignDialog(false)}
        onAssigned={() => {
          setBulkAssignDialog(false);
          setSelectedIds(new Set());
          fetchVehicles();
        }}
      />

      {/* Şub 2026 — Bulk Vites Update Dialog */}
      <Dialog open={bulkTransDialog} onOpenChange={setBulkTransDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TransmissionBadge value={bulkTransValue} size={16} />
              Toplu Vites Güncelle
            </DialogTitle>
            <DialogDescription>
              {selectedIds.size} araç için vites tipi ayarlanacak.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {[{ v: 'AUTOMATIC', label: 'Otomatik' }, { v: 'MANUAL', label: 'Manuel' }, { v: '', label: 'Temizle' }].map(opt => (
                <button
                  key={opt.v || 'clear'}
                  type="button"
                  onClick={() => setBulkTransValue(opt.v)}
                  className={`rounded-lg border p-3 flex flex-col items-center gap-1 transition ${
                    bulkTransValue === opt.v ? 'ring-2 ring-indigo-500 border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-slate-200 hover:border-slate-300 dark:border-slate-700'
                  }`}
                  data-testid={`bulk-trans-opt-${opt.v || 'clear'}`}
                >
                  {opt.v ? <TransmissionBadge value={opt.v} size={18} /> : <span className="text-xl text-slate-400">—</span>}
                  <span className="text-xs font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkTransDialog(false)}>{t('common.cancel')}</Button>
            <Button
              disabled={bulkTransSaving}
              onClick={async () => {
                if (selectedIds.size === 0) return;
                setBulkTransSaving(true);
                let ok = 0, fail = 0;
                for (const id of selectedIds) {
                  try {
                    await api.put(`/vehicles/${id}`, { transmission: bulkTransValue || null });
                    ok += 1;
                  } catch {
                    fail += 1;
                  }
                }
                setBulkTransSaving(false);
                setBulkTransDialog(false);
                setSelectedIds(new Set());
                fetchVehicles();
                toast.success(`${ok} araç güncellendi${fail ? `, ${fail} hata` : ''}`);
              }}
              data-testid="bulk-trans-apply"
            >
              {bulkTransSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Uygula
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirm Dialog */}
      <Dialog open={bulkDeleteConfirm} onOpenChange={() => setBulkDeleteConfirm(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {t('vehicles.bulkDeleteTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-3">
              <span className="font-bold text-red-600">{selectedIds.size}</span> {t('vehicles.bulkDeleteMessage')}
            </p>
            <div className="max-h-32 overflow-y-auto bg-slate-50 dark:bg-slate-800 rounded p-2 text-sm space-y-1">
              {paginatedVehicles.filter(v => selectedIds.has(v.id)).map(v => (
                <div key={v.id} className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">{v.license_plate}</Badge>
                  <span className="text-slate-500">{v.brand} {v.model}</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-confirm">
              {bulkDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedIds.size} {t('vehicles.deleteCountBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand Icons Management Dialog */}
      <Dialog open={brandIconsDialog} onOpenChange={setBrandIconsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="brand-icons-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-blue-500" />
              {t('vehicles.brandIconsTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Upload new */}
            <div className="p-3 rounded-lg border border-dashed border-blue-200 bg-blue-50/30">
              <p className="text-xs font-medium text-slate-600 mb-2">{t('vehicles.brandIconUpload')}</p>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">{t('vehicles.brandName')}</Label>
                  <Input
                    placeholder="Örn: RENAULT"
                    value={brandIconUploadName}
                    onChange={e => setBrandIconUploadName(e.target.value.toUpperCase())}
                    className="h-8 text-sm"
                    data-testid="brand-icon-name-input"
                  />
                </div>
                <label>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => {
                      if (e.target.files?.[0] && brandIconUploadName.trim()) {
                        handleBrandIconUpload(e.target.files[0], brandIconUploadName);
                        e.target.value = '';
                      } else if (!brandIconUploadName.trim()) {
                        toast.error('Önce marka adını giriniz');
                      }
                    }}
                    data-testid="brand-icon-file-input"
                  />
                  <Button variant="default" size="sm" asChild disabled={brandIconUploading} className="h-8">
                    <span>
                      {brandIconUploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                      Yükle
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Existing icons */}
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">{t('vehicles.uploadedIcons')} ({Object.keys(brandIcons).length})</p>
              {Object.keys(brandIcons).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">{t('vehicles.noIconsYet')}</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(brandIcons).sort((a, b) => a[0].localeCompare(b[0])).map(([brand, icon]) => (
                    <div key={brand} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 group" data-testid={`brand-icon-${brand}`}>
                      <img
                        src={`${API_URL}/api/brand-icons/${encodeURIComponent(brand)}/image`}
                        alt={brand}
                        className="w-10 h-10 rounded-lg object-contain bg-white border border-slate-200 p-0.5"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{brand}</p>
                        <p className="text-[10px] text-slate-400">{icon.file_name}</p>
                      </div>
                      {/* Replace icon */}
                      <label>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={e => {
                            if (e.target.files?.[0]) {
                              handleBrandIconUpload(e.target.files[0], brand);
                              e.target.value = '';
                            }
                          }}
                        />
                        <Button variant="outline" size="sm" asChild className="h-7 text-xs">
                          <span><Upload className="h-3 w-3 mr-1" />{t('vehicles.replaceIcon')}</span>
                        </Button>
                      </label>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleBrandIconDelete(brand)}
                        data-testid={`delete-brand-icon-${brand}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vergunning Codes Management Dialog */}
      <Dialog open={vergCodesDialog} onOpenChange={setVergCodesDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-teal-600" />
              Vergunning Kodları Yönetimi
              <span className="text-xs font-normal text-slate-400 ml-auto">Toplam: {vergCodes.length}</span>
            </DialogTitle>
          </DialogHeader>

          {/* Add new code with prefix + integer + optional file */}
          <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-2">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Firma</Label>
                <Select value={newVergFirm} onValueChange={(v) => { setNewVergFirm(v); setNewVergCode(''); }}>
                  <SelectTrigger className="h-9" data-testid="verg-new-firm"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                  <SelectContent>
                    {firms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500">Kod</Label>
                <div className="flex items-stretch h-9 rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                  <span className="px-2.5 flex items-center text-sm font-mono font-semibold bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-r border-input min-w-[42px] justify-center" data-testid="verg-new-prefix">
                    {newVergFirm ? getFirmPrefix(newVergFirm) : '—'}
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={newVergCode}
                    onChange={e => setNewVergCode(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="1"
                    className="flex-1 px-2 bg-transparent text-sm font-mono outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!newVergFirm}
                    onKeyDown={e => e.key === 'Enter' && addVergCode()}
                    data-testid="verg-new-number"
                  />
                </div>
              </div>
              <Button
                size="sm"
                className="h-9 bg-teal-600 hover:bg-teal-700"
                onClick={addVergCode}
                disabled={vergSaving || !newVergCode.trim() || !newVergFirm}
                data-testid="verg-new-submit"
              >
                {vergSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Label className="text-xs text-slate-500 shrink-0 w-16">Belge:</Label>
              <label className="flex-1 flex items-center gap-2 h-8 px-2 text-xs rounded border border-dashed border-slate-300 dark:border-slate-700 hover:border-teal-400 cursor-pointer bg-white dark:bg-slate-900 transition-colors">
                <Upload className="h-3.5 w-3.5 text-slate-400" />
                <span className="truncate flex-1">
                  {newVergFile ? (
                    <span className="text-teal-700 font-medium">{newVergFile.name}</span>
                  ) : (
                    <span className="text-slate-400">PDF/Görsel seç (opsiyonel) — fiziksel yüklenir</span>
                  )}
                </span>
                {newVergFile && (
                  <X
                    className="h-3.5 w-3.5 text-red-400 hover:text-red-600"
                    onClick={(e) => { e.preventDefault(); setNewVergFile(null); }}
                  />
                )}
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,image/*"
                  onChange={e => setNewVergFile(e.target.files?.[0] || null)}
                  data-testid="verg-new-file"
                />
              </label>
            </div>
            {newVergFirm && newVergCode && (
              <p className="text-[11px] text-slate-500 pl-1">
                Eklenecek kod: <span className="font-mono font-semibold text-teal-700">{getFirmPrefix(newVergFirm)}{newVergCode}</span>
              </p>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 mt-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder="Kod ara..."
                value={vergSearch}
                onChange={e => setVergSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
            <Select value={vergFirmFilter || "all"} onValueChange={v => setVergFirmFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-48 text-sm"><SelectValue placeholder="Tüm Firmalar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Firmalar</SelectItem>
                {firms.filter(f => vergCodes.some(c => c.firm_id === f.id)).map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.name} ({vergCodes.filter(c => c.firm_id === f.id).length})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Codes list grouped by firm */}
          <div className="space-y-3 mt-3 overflow-y-auto flex-1 pr-1">
            {firms.map(firm => {
              if (vergFirmFilter && vergFirmFilter !== firm.id) return null;
              const q = vergSearch.trim();
              const firmCodes = vergCodes
                .filter(c => c.firm_id === firm.id)
                .filter(c => !q || turkishIncludes(c.code, q));
              if (firmCodes.length === 0) return null;
              return (
                <div key={firm.id}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                    {firm.name}
                    <span className="text-[10px] font-normal text-slate-400">({firmCodes.length})</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {firmCodes.map(vc => (
                      <span key={vc.id} className="inline-flex items-center justify-between gap-1 text-sm font-mono px-2 py-1 rounded-lg bg-teal-50 text-teal-700 border border-teal-200 group" data-testid={`verg-code-${vc.code}`}>
                        <span className="truncate flex-1 flex items-center gap-1.5">
                          <span>{vc.code}</span>
                          {vc.vehicle_count > 0 ? (
                            <span
                              className="inline-flex items-center gap-0.5 text-[10px] font-sans font-medium px-1.5 py-0 rounded-full bg-white text-teal-700 border border-teal-300"
                              title={`${vc.vehicle_count} araçta kullanılıyor`}
                              data-testid={`verg-usage-${vc.code}`}
                            >
                              <Car className="h-2.5 w-2.5" />{vc.vehicle_count}
                            </span>
                          ) : (
                            <span
                              className="text-[10px] font-sans font-normal px-1.5 py-0 rounded-full bg-slate-100 text-slate-400 border border-slate-200"
                              title="Henüz hiçbir araçta kullanılmıyor"
                              data-testid={`verg-usage-${vc.code}`}
                            >
                              boş
                            </span>
                          )}
                        </span>
                        {vc.file_name ? (
                          <button
                            onClick={() => previewVergCodeFile(vc)}
                            className="text-teal-600 hover:text-teal-800 shrink-0"
                            title={`Önizle: ${vc.file_name}`}
                            data-testid={`verg-preview-${vc.code}`}
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <label
                            className="shrink-0 text-slate-400 hover:text-teal-600 cursor-pointer"
                            title="Belge yükle"
                            data-testid={`verg-upload-${vc.code}`}
                          >
                            <Upload className="h-3.5 w-3.5" />
                            <input
                              type="file"
                              accept=".pdf,image/*"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                if (f) uploadFileToExistingCode(vc.id, f);
                              }}
                            />
                          </label>
                        )}
                        <button onClick={() => deleteVergCode(vc.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity shrink-0" title="Sil">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            {vergCodes.length === 0 && (
              <p className="text-center text-sm text-slate-400 py-4">Henüz vergunning kodu eklenmemiş</p>
            )}
            {vergCodes.length > 0 && (vergSearch || vergFirmFilter) && !firms.some(firm => {
              if (vergFirmFilter && vergFirmFilter !== firm.id) return false;
              const q = vergSearch.trim();
              return vergCodes.filter(c => c.firm_id === firm.id).some(c => !q || turkishIncludes(c.code, q));
            }) && (
              <p className="text-center text-sm text-slate-400 py-4">Aramanıza uygun kod bulunamadı</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Vergunning Assignment Dialog */}
      <Dialog open={bulkVergDialog} onOpenChange={setBulkVergDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-teal-600" />
              Vergunning Toplu Atama
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Seçili <span className="font-semibold text-teal-700">{selectedIds.size}</span> araç için işlem yapacaksınız.
            </p>
          </DialogHeader>

          {/* Mode selector */}
          <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <button
              onClick={() => setBulkVergMode('add')}
              className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-colors ${bulkVergMode === 'add' ? 'bg-white dark:bg-slate-900 text-teal-700 shadow-sm' : 'text-slate-500'}`}
            >
              ➕ Ekle (mevcutları korur)
            </button>
            <button
              onClick={() => setBulkVergMode('remove')}
              className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-colors ${bulkVergMode === 'remove' ? 'bg-white dark:bg-slate-900 text-red-700 shadow-sm' : 'text-slate-500'}`}
            >
              ➖ Çıkar
            </button>
          </div>

          {/* Selected codes preview */}
          {bulkVergSelected.length > 0 && (
            <div className="flex flex-wrap gap-1 p-2 bg-slate-50 dark:bg-slate-800 rounded-md">
              {bulkVergSelected.map(code => (
                <span key={code} className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded bg-teal-100 text-teal-700">
                  {code}
                  <button onClick={() => setBulkVergSelected(prev => prev.filter(c => c !== code))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Command multi-select */}
          <div className="border rounded-md">
            <Command filter={(value, search) => {
              if (!search) return 1;
              return turkishIncludes(value, search) ? 1 : 0;
            }}>
              <CommandInput placeholder="Kod veya firma ara..." className="h-9" />
              <CommandList className="max-h-60">
                <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                {firms.map(firm => {
                  const firmCodes = vergCodes.filter(c => c.firm_id === firm.id);
                  if (firmCodes.length === 0) return null;
                  return (
                    <CommandGroup key={firm.id} heading={firm.name}>
                      {firmCodes.map(vc => {
                        const active = bulkVergSelected.includes(vc.code);
                        return (
                          <CommandItem
                            key={vc.id}
                            value={`${vc.code} ${firm.name}`}
                            onSelect={() => {
                              setBulkVergSelected(prev => active ? prev.filter(c => c !== vc.code) : [...prev, vc.code]);
                            }}
                            className="cursor-pointer"
                          >
                            <div className={`flex h-4 w-4 items-center justify-center rounded border ${active ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}>
                              {active && <Check className="h-3 w-3" />}
                            </div>
                            <span className="font-mono text-sm">{vc.code}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>
          </div>

          <div className="flex items-center justify-end gap-2 mt-2">
            <Button variant="ghost" size="sm" onClick={() => setBulkVergDialog(false)}>
              İptal
            </Button>
            <Button
              size="sm"
              className={bulkVergMode === 'add' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={handleBulkVergSubmit}
              disabled={bulkVergSaving || bulkVergSelected.length === 0}
              data-testid="bulk-verg-submit"
            >
              {bulkVergSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {bulkVergMode === 'add' ? 'Kodları Ekle' : 'Kodları Çıkar'} ({selectedIds.size} araç)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vergunning Report Dialog */}
      <Dialog open={vergReportDialog} onOpenChange={setVergReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              Vergunning Raporu
              {vergReportCode && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  / Kod: <span className="font-mono font-semibold text-indigo-700">{vergReportCode}</span>
                </span>
              )}
              {vergReportData && (
                <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={() => { setVergReportCode(''); setVergReportData(null); setVergReportSearch(''); }}>
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Kod listesine dön
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Stage 1: Code list */}
          {!vergReportCode && (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  placeholder="Kod ara... (örn. H-1)"
                  value={vergReportSearch}
                  onChange={e => setVergReportSearch(e.target.value)}
                  className="pl-8 h-9 text-sm"
                  data-testid="verg-report-search"
                />
              </div>
              <div className="flex-1 overflow-y-auto mt-2 border rounded-md">
                {vergReportSummary.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 py-8">Hiçbir araca atanmış vergunning kodu yok.</p>
                ) : (
                  <Table>
                    <TableHeader className="sticky top-0 bg-slate-50 dark:bg-slate-800 z-10">
                      <TableRow>
                        <TableHead className="w-28">Kod</TableHead>
                        <TableHead className="w-20 text-center">Araç</TableHead>
                        <TableHead>Plakalar</TableHead>
                        <TableHead className="w-20 text-center">Belge</TableHead>
                        <TableHead className="w-24 text-right">Eksik</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vergReportSummary
                        .filter(r => !vergReportSearch.trim() || turkishIncludes(r.code, vergReportSearch) || (r.plates || []).some(p => turkishIncludes(p, vergReportSearch)))
                        .map(r => {
                          const missing = r.vehicle_count - r.document_count;
                          const plates = r.plates || [];
                          return (
                            <TableRow
                              key={r.code}
                              className="cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                              onClick={() => loadVergReportDetail(r.code)}
                              data-testid={`verg-report-row-${r.code}`}
                            >
                              <TableCell className="font-mono font-semibold text-indigo-700">{r.code}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="secondary" className="bg-slate-100 text-slate-700">{r.vehicle_count}</Badge>
                              </TableCell>
                              <TableCell>
                                {plates.length === 0 ? (
                                  <span className="text-xs text-slate-400 italic">—</span>
                                ) : (
                                  <div className="flex flex-wrap gap-1" data-testid={`verg-report-plates-${r.code}`}>
                                    {(r.vehicles || []).slice(0, 6).map(veh => {
                                      const hasDoc = !!veh.has_document;
                                      return (
                                        <Link
                                          key={veh.id || veh.license_plate}
                                          to={`/dashboard/vehicles?search=${encodeURIComponent(veh.license_plate)}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-mono font-semibold ${
                                            hasDoc
                                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300'
                                              : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300'
                                          }`}
                                          title={hasDoc ? `${veh.license_plate} — Belge yüklü` : `${veh.license_plate} — Belge yok`}
                                          data-testid={`verg-report-plate-${r.code}-${veh.license_plate}`}
                                          data-has-doc={hasDoc ? '1' : '0'}
                                        >
                                          {hasDoc ? (
                                            <FileCheck className="h-3 w-3" />
                                          ) : (
                                            <FileText className="h-3 w-3 opacity-70" />
                                          )}
                                          {veh.license_plate}
                                        </Link>
                                      );
                                    })}
                                    {(r.vehicles || []).length > 6 && (
                                      <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300" title={(r.vehicles || []).slice(6).map(v => `${v.license_plate}${v.has_document ? ' ✓' : ''}`).join(', ')}>
                                        +{(r.vehicles || []).length - 6}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="secondary" className="bg-teal-100 text-teal-700">{r.document_count}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {missing > 0 ? (
                                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">{missing} eksik</Badge>
                                ) : (
                                  <span className="text-xs text-slate-400">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right text-slate-400"><ChevronRight className="h-4 w-4 ml-auto" /></TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">Detay görmek için bir koda tıklayın. "Eksik" sütunu, kodu atanmış olup ilgili belge yüklenmemiş araç sayısıdır.</p>
              <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1">
                <span className="inline-flex items-center gap-1">
                  <FileCheck className="h-3 w-3 text-emerald-600" /> Belge yüklü
                </span>
                <span className="inline-flex items-center gap-1">
                  <FileText className="h-3 w-3 text-amber-600" /> Belge yok
                </span>
              </div>
            </>
          )}

          {/* Stage 2: Code detail */}
          {vergReportCode && (
            <>
              {vergReportLoading && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                </div>
              )}
              {!vergReportLoading && vergReportData && (
                <>
                  <div className="flex items-center gap-3 text-sm p-2 bg-slate-50 dark:bg-slate-800 rounded-md">
                    <span><span className="font-semibold">{vergReportData.total_vehicles}</span> araç</span>
                    <span className="text-slate-300">•</span>
                    <span><span className="font-semibold">{vergReportData.total_documents}</span> belge</span>
                    {vergReportData.vehicles_without_document > 0 && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span className="text-amber-600"><span className="font-semibold">{vergReportData.vehicles_without_document}</span> araçta belge eksik</span>
                      </>
                    )}
                    <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={exportVergReportCsv} data-testid="verg-report-export">
                      <FileText className="h-3.5 w-3.5 mr-1" /> CSV İndir
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto border rounded-md">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-50 dark:bg-slate-800 z-10">
                        <TableRow>
                          <TableHead className="w-32">Plaka</TableHead>
                          <TableHead>Araç</TableHead>
                          <TableHead className="w-32">Firma</TableHead>
                          <TableHead>Belgeler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vergReportData.vehicles.map(v => (
                          <TableRow key={v.id}>
                            <TableCell className="font-mono font-semibold">{v.license_plate || '—'}</TableCell>
                            <TableCell className="text-sm">
                              {[v.brand, v.model].filter(Boolean).join(' ')}
                              {v.year && <span className="text-xs text-slate-400 ml-1">({v.year})</span>}
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">{v.firm_name || '—'}</TableCell>
                            <TableCell>
                              {v.documents.length === 0 ? (
                                <span className="text-xs text-amber-600 italic">Belge yüklenmemiş</span>
                              ) : (
                                <div className="space-y-1">
                                  {v.documents.map(d => (
                                    <div key={d.id} className="flex items-center gap-2 text-xs">
                                      <FileText className="h-3.5 w-3.5 text-teal-600 shrink-0" />
                                      <span className="truncate max-w-[260px]" title={d.file_name}>{d.file_name}</span>
                                      {d.expiry_date && (
                                        <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-[10px]">
                                          Son: {d.expiry_date}
                                        </Badge>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Vergunning Code File Preview — uses shared DocumentPreviewModal */}
      <DocumentPreviewModal
        open={!!vergPreviewDoc}
        onClose={() => setVergPreviewDoc(null)}
        document={vergPreviewDoc}
        downloadPath={vergPreviewDoc ? `/vergunning-codes/${vergPreviewDoc.id}/file` : undefined}
        title={vergPreviewDoc?.file_name}
      />

      {/* Vergunning Code Delete Confirmation */}
      <Dialog open={!!vergDeleteConfirm} onOpenChange={(open) => !open && setVergDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {vergDeleteConfirm?.usage?.count > 0 ? (
                <AlertTriangle className="h-5 w-5 text-red-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              Vergunning kodunu sil
            </DialogTitle>
          </DialogHeader>
          {vergDeleteConfirm && (
            <div className="space-y-3 py-2 text-sm">
              {vergDeleteConfirm.usage.count > 0 ? (
                <>
                  <div className="p-3 rounded-md border-2 border-red-300 bg-red-50 dark:bg-red-950/30">
                    <p className="font-semibold text-red-700 mb-1 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Dikkat! Bu kod hâlâ kullanımda.
                    </p>
                    <p className="text-red-600 text-xs leading-relaxed">
                      <span className="font-mono font-semibold">{vergDeleteConfirm.code}</span> kodu şu anda
                      <span className="font-semibold"> {vergDeleteConfirm.usage.count} araçta</span> atanmış.
                      Silerseniz bu kodlar tüm araçlardan otomatik kaldırılacak ve kod tamamen silinecek.
                    </p>
                  </div>
                  <div className="max-h-32 overflow-y-auto border rounded-md p-2 bg-slate-50 dark:bg-slate-900">
                    <p className="text-xs text-slate-500 mb-1">Etkilenecek araçlar:</p>
                    <ul className="text-xs space-y-0.5">
                      {vergDeleteConfirm.usage.vehicles.map(v => (
                        <li key={v.id} className="flex items-center gap-2 font-mono">
                          <Car className="h-3 w-3 text-slate-400" />
                          <span className="font-semibold">{v.license_plate}</span>
                          {v.label && <span className="text-slate-500">— {v.label}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {vergDeleteConfirm.hasFile && (
                    <p className="text-xs text-amber-600 flex items-start gap-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      Bu koda yüklenmiş belge de kalıcı olarak silinecek.
                    </p>
                  )}
                </>
              ) : (
                <div className="p-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30">
                  <p className="text-amber-700">
                    <span className="font-mono font-semibold">{vergDeleteConfirm.code}</span> kodunu silmek istediğinize emin misiniz?
                  </p>
                  {vergDeleteConfirm.hasFile ? (
                    <p className="text-xs text-amber-600 mt-1.5 flex items-start gap-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      Yüklenmiş belge de kalıcı olarak silinecek.
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">Bu kod şu anda hiçbir araçta kullanılmıyor.</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setVergDeleteConfirm(null)} disabled={vergDeleting} data-testid="verg-delete-cancel">
              İptal
            </Button>
            <Button
              size="sm"
              className={vergDeleteConfirm?.usage?.count > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
              onClick={confirmDeleteVergCode}
              disabled={vergDeleting}
              data-testid="verg-delete-confirm"
            >
              {vergDeleting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
              {vergDeleteConfirm?.usage?.count > 0 ? 'Yine de sil' : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- HELPER COMPONENTS ----

function SortableHeader({ field, label, sortField, sortDirection, onSort }) {
  const isActive = sortField === field;
  return (
    <TableHead
      className="cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-blue-600" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-300" />
        )}
      </div>
    </TableHead>
  );
}
